import clsx from "clsx";
import { isFinite } from "lodash";
import { memo } from "react";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { PrimitiveValue, RecursiveDataRecord } from "react-migration/lib/map/GenericGeoJsonLayer/types";
import { isDefined } from "react-migration/lib/util/isDefined";

type GenericDataDisplayGroupedItemsRecursiveProps = {
  data?: RecursiveDataRecord;
  outerGroupClassName?: string;
  labelTranslationNamespace?: string;
  hideEmptyValues?: boolean;
};

export const GenericDataDisplayGroupedItemsRecursive = memo(
  ({
    data,
    outerGroupClassName,
    labelTranslationNamespace,
    hideEmptyValues = false,
  }: GenericDataDisplayGroupedItemsRecursiveProps) => {
    const { t: translateLabel, checkKey: checkLabelKey } = useTranslation(labelTranslationNamespace);

    const renderEntry = (key: string, value: PrimitiveValue | RecursiveDataRecord, isNested = false) => {
      if (isRecursiveDataRecord(value)) {
        return renderGroup(key, value, isNested);
      }

      return renderLeaf(key, value);
    };

    const renderGroup = (key: string, value: RecursiveDataRecord, isNested = false) => {
      const isTopLevelGroup = !isNested;

      const groupWrapperClassName = clsx("flex flex-col gap-2", {
        [outerGroupClassName ?? ""]: isTopLevelGroup,
        "not-first:mt-4": isTopLevelGroup,
        "not-first:mt-2": isNested,
      });

      const headingClassName = clsx("text-text-base-primary", {
        "text-xs font-normal text-text-base-secondary-a": isTopLevelGroup,
        "text-xs font-bold text-text-base-primary": !isTopLevelGroup,
      });

      return (
        <article key={key} className={groupWrapperClassName}>
          <h1 className={headingClassName}>{key}</h1>
          <div>
            {Object.entries(value).map(([nestedKey, nestedValue]) => renderEntry(nestedKey, nestedValue, true))}
          </div>
        </article>
      );
    };

    const renderLeaf = (key: string, value: PrimitiveValue) => {
      if (hideEmptyValues && !isDefined(value)) {
        return null;
      }

      return (
        <div key={key} className="flex flex-row justify-between not-first:mt-2">
          <span className="text-xs font-medium text-text-base-secondary shrink-0">
            {checkLabelKey(key) ? translateLabel(key) : key}
          </span>
          <ValueDisplay value={value} />
        </div>
      );
    };

    if (!data || !Object.keys(data).length) {
      return null;
    }

    return <div className="w-full">{Object.entries(data).map(([key, value]) => renderEntry(key, value))}</div>;
  }
);

GenericDataDisplayGroupedItemsRecursive.displayName = "GenericDataDisplayGroupedItemsRecursive";

function isRecursiveDataRecord(value: PrimitiveValue | RecursiveDataRecord): value is RecursiveDataRecord {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function ValueDisplay({ value }: { value: PrimitiveValue }) {
  if (!isDefined(value)) return null;

  if (isFinite(Number(value))) {
    const number = Number(value);
    const isInteger = Number.isInteger(number);

    return (
      <span className="text-xs font-semibold text-text-base-secondary">
        {isInteger ? number : number.toPrecision(4)}
      </span>
    );
  }

  return <span className="text-xs font-semibold text-text-base-secondary">{value}</span>;
}
